import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";
import PolicyIcon from "@mui/icons-material/Policy";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import { API } from "../../../api/API";

const EditContratModal = ({ open, onClose, contrat, onRefresh }) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [polices, setPolices] = useState([]);
  const [assures, setAssures] = useState([]);

  useEffect(() => {
    const fetchPolices = async () => {
      try {
        const response = await API.get("/polices");
        if (response.data && response.data.data) {
          setPolices(response.data.data);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des polices:", error);
        toast.error("Impossible de récupérer la liste des polices");
      }
    };

    const fetchAssures = async () => {
      try {
        const response = await API.get("/assures");
        if (response.data && response.data.data) {
          setAssures(response.data.data);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des assurés:", error);
        toast.error("Impossible de récupérer la liste des assurés");
      }
    };

    fetchPolices();
    fetchAssures();
  }, []);

  // Schéma de validation Yup
  const validationSchema = Yup.object({
    police_id: Yup.number().required("La police est requise"),
    assure_id: Yup.number().required("L'assuré est requis"),
    date_debut: Yup.date().required("La date de début est requise"),
    date_fin: Yup.date().nullable(),
    montant: Yup.number().nullable().positive("Le montant doit être positif"),
    statut: Yup.boolean(),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsLoading(true);
    try {
      const response = await API.put(
        `/contrats-assurances/${contrat.id}`,
        values
      );
      if (response.status === 200) {
        toast.success("Contrat mis à jour avec succès");
        if (onRefresh) {
          onRefresh();
        }
        onClose();
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour du contrat:", error);
      toast.error(
        error.response?.data?.message ||
          "Erreur lors de la mise à jour du contrat"
      );
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  if (!contrat) return null;

  const initialValues = {
    police_id: contrat.police_id || "",
    assure_id: contrat.assure_id || "",
    date_debut: contrat.date_debut ? dayjs(contrat.date_debut) : null,
    date_fin: contrat.date_fin ? dayjs(contrat.date_fin) : null,
    montant: contrat.montant || "",
    statut: contrat.statut || false,
  };

  return (
    <Modal
      show={open}
      onHide={onClose}
      size="lg"
      centered
      backdrop="static"
      aria-labelledby="edit-contrat-modal"
    >
      <ToastContainer />
      <Modal.Header closeButton>
        <Typography
          variant="h5"
          component="h2"
          fontWeight="bold"
          color="#37B049"
        >
          Modifier le Contrat #{contrat.id}
        </Typography>
      </Modal.Header>

      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting,
          }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background:
                        "linear-gradient(135deg, rgba(55, 176, 73, 0.05) 0%, rgba(255, 255, 255, 0) 100%)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <PolicyIcon sx={{ mr: 1, color: "#37B049" }} />
                      <Typography variant="h6" color="#37B049" fontWeight={600}>
                        Information du Contrat
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          error={touched.police_id && Boolean(errors.police_id)}
                        >
                          <InputLabel id="police-select-label">
                            Police
                          </InputLabel>
                          <Select
                            labelId="police-select-label"
                            id="police_id"
                            name="police_id"
                            value={values.police_id}
                            label="Police"
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            {polices.map((police) => (
                              <MenuItem key={police.id} value={police.id}>
                                {police.label} - {police.numero}
                              </MenuItem>
                            ))}
                          </Select>
                          {touched.police_id && errors.police_id && (
                            <FormHelperText>{errors.police_id}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          error={touched.assure_id && Boolean(errors.assure_id)}
                        >
                          <InputLabel id="assure-select-label">
                            Assuré
                          </InputLabel>
                          <Select
                            labelId="assure-select-label"
                            id="assure_id"
                            name="assure_id"
                            value={values.assure_id}
                            label="Assuré"
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            {assures.map((assure) => (
                              <MenuItem key={assure.id} value={assure.id}>
                                {assure.nom} {assure.prenom}
                              </MenuItem>
                            ))}
                          </Select>
                          {touched.assure_id && errors.assure_id && (
                            <FormHelperText>{errors.assure_id}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background:
                        "linear-gradient(135deg, rgba(55, 176, 73, 0.05) 0%, rgba(255, 255, 255, 0) 100%)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <CalendarMonthIcon sx={{ mr: 1, color: "#37B049" }} />
                      <Typography variant="h6" color="#37B049" fontWeight={600}>
                        Période et Montant
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Date de début"
                            value={values.date_debut}
                            onChange={(newValue) => {
                              setFieldValue("date_debut", newValue);
                            }}
                            slotProps={{
                              textField: {
                                variant: "outlined",
                                fullWidth: true,
                                error:
                                  touched.date_debut &&
                                  Boolean(errors.date_debut),
                                helperText:
                                  touched.date_debut && errors.date_debut,
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Date de fin"
                            value={values.date_fin}
                            onChange={(newValue) => {
                              setFieldValue("date_fin", newValue);
                            }}
                            slotProps={{
                              textField: {
                                variant: "outlined",
                                fullWidth: true,
                                error:
                                  touched.date_fin && Boolean(errors.date_fin),
                                helperText: touched.date_fin && errors.date_fin,
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          id="montant"
                          name="montant"
                          label="Montant (FCFA)"
                          type="number"
                          value={values.montant}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.montant && Boolean(errors.montant)}
                          helperText={touched.montant && errors.montant}
                          InputProps={{
                            startAdornment: (
                              <Box sx={{ mr: 1, color: "text.secondary" }}>
                                <AttachMoneyIcon />
                              </Box>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.statut}
                                onChange={(e) =>
                                  setFieldValue("statut", e.target.checked)
                                }
                                name="statut"
                                color="success"
                              />
                            }
                            label={
                              <Typography variant="body1">
                                {values.statut
                                  ? "Contrat Actif"
                                  : "Contrat Inactif"}
                              </Typography>
                            }
                          />
                          <InfoIcon
                            sx={{
                              ml: 1,
                              color: "text.secondary",
                              fontSize: "1.2rem",
                            }}
                            titleAccess="Un contrat actif permet à l'assuré de bénéficier des services associés à sa police"
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={onClose}
                  startIcon={<CancelIcon />}
                  sx={{
                    borderColor: "#d32f2f",
                    color: "#d32f2f",
                    "&:hover": {
                      borderColor: "#b71c1c",
                      backgroundColor: "rgba(211, 47, 47, 0.04)",
                    },
                  }}
                  disabled={isLoading || isSubmitting}
                >
                  Annuler
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    isLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={isLoading || isSubmitting}
                  sx={{
                    backgroundColor: "#37B049",
                    "&:hover": { backgroundColor: "#2e8b3a" },
                  }}
                >
                  {isLoading ? "Enregistrement..." : "Enregistrer"}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default EditContratModal;
